/**
 * Popover multi-select de modelos destino (mesmo visual do filtro de equipamentos).
 */
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconLink, IconLinkOff } from '@tabler/icons-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import '../../board/board-search-filters/BoardSearchFilters.css';

const PICKER_WIDTH = 520;
const PICKER_MAX_HEIGHT = 360;

export default function CompatExportModeloPopover({
  anchorRef,
  isOpen,
  onClose,
  onConfirm,
  options = [],
  linkedByModelo,
  leasedByModelo,
  selected = [],
  onSelectedChange,
  confirming = false,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [layout, setLayout] = useState(null);

  const items = useMemo(() => {
    const q = String(searchTerm || '').trim().toLowerCase();
    const base = (options || []).map((value) => ({ value, label: value }));
    if (!q) return base;
    return base.filter((item) => String(item.label || '').toLowerCase().includes(q));
  }, [options, searchTerm]);

  const selectableValues = useMemo(() => items.map((i) => i.value), [items]);

  const updateLayout = () => {
    const el = anchorRef?.current;
    if (!el || !isOpen) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(computeToolbarFilterPopoverLayout(rect, PICKER_WIDTH, PICKER_MAX_HEIGHT));
  };

  useLayoutEffect(() => {
    updateLayout();
  }, [isOpen, options.length, selected.length, anchorRef]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      return undefined;
    }
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef?.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen, onClose, anchorRef]);

  const handleToggle = (item) => {
    const next = new Set(selected || []);
    if (next.has(item.value)) next.delete(item.value);
    else next.add(item.value);
    onSelectedChange?.([...next]);
  };

  const handleBulkToggleAll = () => {
    if (!selectableValues.length) return;
    const allMarked = selectableValues.every((v) => (selected || []).includes(v));
    onSelectedChange?.(allMarked ? [] : [...selectableValues]);
  };

  if (!isOpen || !layout) return null;

  return createPortal(
    <div
      className="board-search-filters-popover board-search-filters-popover--simple ce-compat-eq-popover vp-mock-export-eq-popover"
      style={{
        position: layout.position,
        top: layout.top,
        left: layout.left,
        width: layout.width,
        boxSizing: layout.boxSizing,
        zIndex: layout.zIndex,
        maxHeight: layout.maxHeight,
        '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
        transform: layout.flipped ? 'translateY(-100%)' : undefined,
      }}
      role="dialog"
      aria-label="Exportar para modelos"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <BoardSearchSimplePickerPanel
        searchPlaceholder="Buscar equipamento"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showSearch={options.length > 8}
        items={items}
        getKey={(i) => i.value}
        getLabel={(i) => i.label}
        getItemGroup={(i) =>
          (linkedByModelo?.get(i.value) || 0) > 0 ? 'Com vínculo' : 'Sem vínculo'
        }
        renderGroupLabel={(label) => (
          <>
            {label === 'Com vínculo' ? (
              <IconLink size={13} stroke={1.75} aria-hidden />
            ) : (
              <IconLinkOff size={13} stroke={1.75} aria-hidden />
            )}
            <span>{label}</span>
          </>
        )}
        showGroupCount={false}
        getSuffix={(i) => {
          const linked = linkedByModelo?.get(i.value);
          const leased = leasedByModelo?.get(i.value);
          return (
            <>
              <GoHoverTooltip text="Peças vinculadas" align="center" delayMs={180}>
                <span className="bsf-simple-picker-item-meta ce-compat-eq-meta ce-compat-eq-meta--links">
                  {linked != null ? String(linked) : '0'}
                </span>
              </GoHoverTooltip>
              <span className="ce-compat-eq-meta-sep" aria-hidden="true" />
              <GoHoverTooltip text="Quantidade locada" align="center" delayMs={180}>
                <span className="bsf-simple-picker-item-meta ce-compat-eq-meta">
                  {leased != null ? String(leased) : '0'}
                </span>
              </GoHoverTooltip>
            </>
          );
        }}
        selectedKeys={selected || []}
        onToggle={handleToggle}
        emptyMessage="Nenhum equipamento encontrado"
        listAriaLabel="Equipamentos"
        bulkMenuEnabled
        onBulkToggleAll={selectableValues.length > 0 ? handleBulkToggleAll : undefined}
      />
      <div className="vp-mock-export-eq-footer">
        <button
          type="button"
          className="user-form-link-btn user-form-footer-btn"
          onClick={onClose}
          disabled={confirming}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="user-form-profile-save-btn user-form-footer-btn"
          disabled={confirming || !(selected || []).length}
          onClick={() => onConfirm?.(selected || [])}
        >
          {confirming ? 'Exportando…' : `Exportar (${(selected || []).length})`}
        </button>
      </div>
    </div>,
    document.body
  );
}
